import { useEffect, useMemo, useRef, useState } from 'react'
import { Security } from '../../../proto/generated/security'
import { GetTechnicalsConfigResponse, TechnicalsTimeframeConfig } from '../../../proto/generated/overview-widget'
import { getSecurityKey } from '../../../util/security'
import { getTechnicalsConfigCached } from './technicalsConfigCache'
import { getTechnicalsCandlesCached } from './technicalsCandleCache'
import { computeTechnicals } from './technicalsEngine'
import { TechnicalsComputedResult } from './types'

const TIMEFRAME_DEBOUNCE_MS = 150

export type UseTechnicalsDataArgs = {
    security: Security | undefined
    timeframeId: string
    enabled?: boolean
}

export type UseTechnicalsDataResult = {
    timeframes: TechnicalsTimeframeConfig[]
    result: TechnicalsComputedResult | null
    loading: boolean
    error: string | null
}

export const useTechnicalsData = ({
    security,
    timeframeId,
    enabled = true
}: UseTechnicalsDataArgs): UseTechnicalsDataResult => {
    const [config, setConfig] = useState<GetTechnicalsConfigResponse | null>(null)
    const [result, setResult] = useState<TechnicalsComputedResult | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const securityRef = useRef(security)
    securityRef.current = security

    const securityKey = security ? getSecurityKey(security.exchange, security.code) : null
    const timeframes = useMemo(() => config?.timeframes ?? [], [config])

    // Config is global — load once when enabled; do not couple to candle fetches.
    useEffect(() => {
        if (!enabled) return

        let cancelled = false
        ;(async () => {
            try {
                const cfg = await getTechnicalsConfigCached()
                if (!cancelled) {
                    setConfig(cfg)
                    setError(null)
                }
            } catch (e) {
                console.error('Failed to load technicals config', e)
                if (!cancelled) setError('Unable to load technicals configuration.')
            }
        })()

        return () => {
            cancelled = true
        }
    }, [enabled])

    // Clear stale result when the symbol changes (keyed by exchange+code, not object identity).
    // `loading` flips here rather than after the debounce so consumers never observe the
    // "settled with no data" state while the next fetch is still pending.
    useEffect(() => {
        setResult(null)
        setError(null)
        setLoading(enabled && !!securityKey)
    }, [enabled, securityKey])

    // Candle compute runs only after config is ready, and only when securityKey / timeframe changes.
    useEffect(() => {
        if (!enabled || !securityKey || !config) {
            if (!enabled || !securityKey) {
                setResult(null)
                setLoading(false)
            }
            return
        }

        let cancelled = false
        let controller: AbortController | null = null

        const debounceTimer = setTimeout(() => {
            controller = new AbortController()
            const current = securityRef.current
            if (!current || cancelled) return

            setLoading(true)
            setError(null)

            ;(async () => {
                try {
                    const timeframe =
                        config.timeframes.find((tf) => tf.id === timeframeId) ?? config.timeframes[0]
                    if (!timeframe) {
                        if (!cancelled) {
                            setError('No timeframe configuration available.')
                            setResult(null)
                        }
                        return
                    }

                    const series = await getTechnicalsCandlesCached(current, timeframe, controller.signal)
                    if (cancelled || controller.signal.aborted) return

                    const computed = computeTechnicals(config, series, timeframe.id)
                    if (!cancelled) setResult(computed)
                } catch (e: any) {
                    if (cancelled || controller.signal.aborted || e?.name === 'AbortError') return
                    console.error('Failed to compute technicals', e)
                    if (!cancelled) {
                        setError('Unable to load technicals data.')
                        setResult(null)
                    }
                } finally {
                    if (!cancelled && !controller.signal.aborted) setLoading(false)
                }
            })()
        }, TIMEFRAME_DEBOUNCE_MS)

        return () => {
            cancelled = true
            clearTimeout(debounceTimer)
            controller?.abort()
        }
    }, [enabled, securityKey, timeframeId, config])

    return { timeframes, result, loading, error }
}
